import { Utils as utils } from '@client/utility';
import * as Sentry from '@sentry/react';
import { useChatStore } from '@client/store';
import { GetUserData, CharacterPayload, Message, GetUserUUID } from '@client/components';

type ChatDataResult = CharacterPayload | { error: string };

interface ChatRow {
  char_uuid: string;
  name: string;
  persona: string;
  title: string;
  profile_image?: string;
  model_instructions: string | null;
  attribute: string | null;
  description?: string;
  scenario?: string;
  message_example?: string;
  lorebook?: string;
  first_message?: string[];
  gender?: string;
}

interface ChatMessageRow {
  id: number;
  user_message: string | null;
  char_message: string | null;
  chat_uuid: string;
  user_id: number;
  char_id: number;
  created_at: string;
  alternative_messages?: string[] | null;
  user_uuid?: string;
}

interface FetchChatDataResult {
  is_error: boolean;
  Character?: CharacterPayload;
  error?: string;
}

export async function getChatData(chatId: string): Promise<ChatDataResult> {
  try {
    if (!chatId?.trim()) return { error: 'Invalid chat ID' };
    const { data: chatData } = await utils.db.select<ChatRow>({
      tables: 'chats',
      columns: `
        char_uuid,
        name,
        persona,
        title,
        profile_image,
        model_instructions,
        attribute,
        description,
        scenario,
        message_example,
        lorebook,
        first_message,
        gender
      `,
      match: { chat_uuid: chatId },
    });
    if (!chatData?.length) return { error: 'Chat not found' };
    const row = chatData[0];
    if (!row.char_uuid || !row.name)
      return { error: 'Invalid chat data: missing required fields' };
    return {
      char_uuid: row.char_uuid,
      name: row.name,
      persona: row.persona ?? '',
      title: row.title ?? '',
      profile_image: row.profile_image ?? '',
      first_message: row.first_message ?? [],
      model_instructions: row.model_instructions ?? '',
      attribute: row.attribute ?? '',
      description: row.description ?? '',
      scenario: row.scenario ?? '',
      message_example: row.message_example ?? '',
      lorebook: row.lorebook ?? '',
      gender: row.gender ?? 'neutral',
    };
  } catch (error) {
    Sentry.captureException(error);
    return { error: 'Unexpected error while fetching chat data' };
  }
}

export const fetchChatData = async (
  chat_uuid: string
): Promise<FetchChatDataResult> => {
  if (!chat_uuid) return { is_error: true, error: 'Missing chat_uuid' };
  try {
    const userData = await GetUserData();
    if ('error' in userData) return { is_error: true, error: userData.error };
    
    const characterData = await getChatData(chat_uuid);
    if ('error' in characterData)
      return { is_error: true, error: characterData.error };

    const user_uuid = await GetUserUUID();

    let dbMessages: ChatMessageRow[] = [];
    try {
      const result = await utils.db.select<ChatMessageRow>({
        tables: 'chat_messages',
        columns: `
          id,
          chat_uuid,
          user_message,
          char_message,
          user_id,
          char_id,
          created_at,
          alternative_messages,
          user_uuid
        `,
        match: { chat_uuid },
        orderBy: { column: 'created_at', ascending: true },
        nocache: true,
      });
      dbMessages = result?.data ?? [];
    } catch (err) {
      Sentry.captureException(err);
      return { is_error: true, error: 'Failed to fetch chat messages' };
    }

    const formattedMessages: Message[] = [];
    const firstMessagesArray = characterData.first_message ?? [];

    const firstMsgText = firstMessagesArray[0];
    const hasFirstMessageInDb = dbMessages.some(m => 
      m.char_message === firstMsgText && m.user_message === null
    );

    if (firstMsgText && !hasFirstMessageInDb) {
      const alternativeFirstMsgs = firstMessagesArray.slice(1);
      
      try {
        const inserted = await utils.db.insert({
          tables: 'chat_messages',
          data: {
            chat_uuid: chat_uuid,
            user_uuid: user_uuid,
            char_message: firstMsgText,
            user_message: null,
            alternative_messages: alternativeFirstMsgs.length > 0 ? alternativeFirstMsgs : null,
          },
        }) as { id: number }[];

        const newFirstMsg: Message = {
          id: inserted[0]?.id ?? -1,
          is_first: true,
          name: characterData.name ?? 'Anon',
          text: firstMsgText,
          type: 'char',
          chat_uuid,
          char_uuid: characterData.char_uuid,
          gender: characterData.gender ?? 'neutral',
          alternative_messages: alternativeFirstMsgs,
          current: 0,
          stop_stream: false,
        };
        
        formattedMessages.push(newFirstMsg);
      } catch (insertErr) {
        formattedMessages.push({
          id: -1,
          is_first: true,
          name: characterData.name ?? 'Anon',
          text: firstMsgText,
          type: 'char',
          chat_uuid,
          char_uuid: characterData.char_uuid,
          gender: characterData.gender ?? 'neutral',
          alternative_messages: alternativeFirstMsgs,
          current: 0,
          stop_stream: false,
        });
      }
    }

    for (const msg of dbMessages) {
      if (msg.user_message) {
        formattedMessages.push({
          id: msg.id,
          name: userData.username ?? 'Anon',
          text: msg.user_message,
          type: 'user',
          chat_uuid: msg.chat_uuid,
          ref_id: msg.id,
          current: 0,
          stop_stream: false,
          alternative_messages: msg.alternative_messages ?? [],
        });
      }
      
      if (msg.char_message) {
        const isFirst = msg.user_message === null && 
          msg.char_message === firstMsgText &&
          dbMessages.indexOf(msg) === 0;
          
        formattedMessages.push({
          id: msg.id,
          is_first: isFirst,
          name: characterData.name ?? 'Anon',
          text: msg.char_message,
          type: 'char',
          chat_uuid: msg.chat_uuid,
          ref_id: msg.id,
          char_uuid: characterData.char_uuid,
          gender: characterData.gender ?? 'neutral',
          alternative_messages: msg.alternative_messages ?? [],
          current: 0,
          stop_stream: false,
        });
      }
    }

    useChatStore.getState().setCharacter(characterData);
    useChatStore.getState().setMessages(formattedMessages);

    return {
      is_error: false,
      Character: characterData,
    };
  } catch (err) {
    Sentry.captureException(
      err instanceof Error ? err : new Error(String(err))
    );
    return {
      is_error: true,
      error: 'An error occurred while fetching chat data',
    };
  }
};